import { RowActions } from "@/shared/ui/RowActions";
import { DataTable, type TanstackReactTable } from "@devhop/ui";
import { EditIcon, TrashIcon } from "lucide-react";
import type { CustomerGroupDTO } from "../api/client";

type CustomerGroupsTableProps = {
  canManage: boolean;
  isLoading: boolean;
  data: CustomerGroupDTO[];
  offset: number;
  limit: number;
  totalCount: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onEdit: (row: CustomerGroupDTO) => void;
  onDelete: (id: string) => void;
  onPaginationChange: (offset: number, limit: number) => void;
  onSortingChange: (id: string, desc: boolean) => void;
};

export function CustomerGroupsTable({
  canManage,
  isLoading,
  data,
  offset,
  limit,
  totalCount,
  sortBy,
  sortOrder,
  onEdit,
  onDelete,
  onPaginationChange,
  onSortingChange,
}: CustomerGroupsTableProps) {
  const columns: TanstackReactTable.ColumnDef<CustomerGroupDTO>[] = [
    {
      id: "nameLo",
      header: "ຊື່ (ລາວ)",
      cell: ({ row }) => {
        const n = row.original.name as { lo?: string };
        const color = row.original.color;
        return (
          <div className="flex items-center gap-2">
            {color && (
              <span
                className="inline-block h-3 w-3 shrink-0 rounded-full border"
                style={{ backgroundColor: color }}
              />
            )}
            <span className="font-medium">{n?.lo ?? "—"}</span>
          </div>
        );
      },
    },
    {
      id: "nameTh",
      header: "ຊື່ (ໄທ)",
      cell: ({ row }) => {
        const n = row.original.name as { th?: string };
        return <span className="text-muted-foreground text-sm">{n?.th ?? "—"}</span>;
      },
    },
    {
      id: "nameEn",
      header: "ຊື່ (ອັງກິດ)",
      cell: ({ row }) => {
        const n = row.original.name as { en?: string };
        return <span className="text-muted-foreground text-sm">{n?.en ?? "—"}</span>;
      },
    },
    {
      id: "description",
      header: "ຄຳອະທິບາຍ",
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.description ?? "—"}
        </span>
      ),
    },
    {
      id: "memberCount",
      header: "ຈຳນວນລູກຄ້າ",
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.memberCount ?? 0}
        </span>
      ),
    },
    {
      id: "sortOrder",
      header: "ລຳດັບ",
      cell: ({ row }) => row.original.sortOrder,
    },
    {
      id: "actions",
      cell: ({ row }) => {
        if (!canManage) return null;
        const actions = [
          {
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="h-4 w-4" />,
            onClick: () => onEdit(row.original),
          },
          {
            label: "ລຶບ",
            icon: <TrashIcon className="h-4 w-4" />,
            onClick: () => onDelete(row.original.id),
            variant: "destructive" as const,
          },
        ];
        return <RowActions actions={actions} maxInline={2} />;
      },
    },
  ];

  return (
    <DataTable<CustomerGroupDTO, unknown>
      noDataMessage="ບໍ່ພົບກຸ່ມລູກຄ້າ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(s) => {
        if (s[0]?.id === "") return;
        onSortingChange(s[0]?.id || "", s[0]?.desc || false);
      }}
    />
  );
}
